import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { BRAND } from "../../lib/content";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.35 + i * 0.12 } }),
};
const MaskLine = ({ children, i }) => (
  <span className="block overflow-hidden">
    <motion.span className="block" variants={line} custom={i} initial="hidden" animate="show">{children}</motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="relative h-screen w-full overflow-hidden grain bg-[#080808]">
      <motion.div style={{ y }} className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_70%_30%,rgba(184,184,184,0.14),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_50%_40%_at_15%_80%,rgba(184,184,184,0.06),transparent_55%)]" />
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)", backgroundSize: "80px 80px" }} />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-transparent to-[#080808]/40" />

      <motion.div style={{ opacity }} className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-8 flex flex-col justify-center">
        <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="overline mb-6" data-testid="hero-overline">
          {BRAND.location} — Paint Correction · Ceramic Coating
        </motion.span>

        <h1 data-testid="hero-headline" className="font-display font-black text-white text-glow text-5xl sm:text-6xl md:text-7xl lg:text-[6rem] leading-[0.92] tracking-tighter max-w-5xl">
          <MaskLine i={0}>Precision</MaskLine>
          <MaskLine i={1}>in every</MaskLine>
          <MaskLine i={2}><span className="text-silver">detail.</span></MaskLine>
        </h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.0 }} className="text-gray-300 text-base md:text-lg font-light max-w-xl mt-8 leading-relaxed" data-testid="hero-subheading">
          Premium automotive detailing and paint protection in Melbourne. From meticulous interior and
          exterior detailing to advanced paint correction and ceramic coating — every service performed
          with precision, care and attention to detail.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15 }} className="flex flex-col sm:flex-row gap-4 mt-10">
          <Link to="/services" data-testid="hero-explore" className="bg-silver hover:bg-white text-black px-8 py-4 border border-silver transition-colors uppercase tracking-widest text-sm font-bold">
            Explore Our Services
          </Link>
          <Link to="/contact" data-testid="hero-book-now" className="bg-transparent text-white px-8 py-4 border border-white/20 hover:border-white transition-colors uppercase tracking-widest text-sm font-bold">
            Book Your Detail
          </Link>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }} className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs md:text-sm text-gray-500 font-mono-tech">
          <span className="text-silver">{BRAND.training}</span>
          <span className="hidden md:inline">·</span>
          <span>Premium Products · Professional Techniques · Exceptional Attention to Detail</span>
        </motion.div>
      </motion.div>

      <motion.div style={{ opacity }} className="absolute bottom-8 right-8 z-10 hidden md:flex items-center gap-2 text-gray-500 font-mono-tech text-[10px] tracking-[0.3em] uppercase">
        Scroll <ArrowDown size={14} className="animate-bounce" />
      </motion.div>
    </section>
  );
};
